import React, { useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  BuildingOfficeIcon,
  BuildingOffice2Icon,
  ChatBubbleLeftRightIcon,
  TicketIcon,
  WrenchScrewdriverIcon,
  CpuChipIcon,
  UserGroupIcon,
  UsersIcon,
  DocumentTextIcon,
  DocumentChartBarIcon,
  PresentationChartLineIcon,
  UserIcon,
  TruckIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  InboxIcon,
  BoltIcon,
  HeartIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Square3Stack3DIcon,
  QrCodeIcon,
  ShoppingCartIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  ChevronDownIcon,
  BookmarkSquareIcon,
  BellIcon,
  ComputerDesktopIcon,
  ListBulletIcon,
  BookOpenIcon,
  CubeIcon,
  FireIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  ArrowsRightLeftIcon,
  UserCircleIcon,
  ChartPieIcon,
  MegaphoneIcon,
  LockClosedIcon,
  WrenchIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/utils/api';
import { hasPermFor } from '@/utils/permissions';
import { useAuthStore } from '@/stores/authStore';
import { canOfferInNav } from '@/config/routeAccess';
import {
  EMPLOYEE_NAV_FOOTER, EMPLOYEE_NAV_GROUPS, isEmployeeExperience } from '@/config/employeeExperience';
import {
  fmActivePath, fmNavGroups, isFmExperience, type FmCountKey } from '@/config/fmExperience';
import { useFmCounts, useFmShell } from '@/hooks/useFmShell';
import { TECH_INCIDENT, isTechShell, techActivePath, techNavGroups } from '@/config/techExperience';
import { isReceptionShell, receptionNavGroups } from '@/config/receptionExperience';
import { SpacioraLogo } from '@/components/Brand/SpacioraLogo';
import {
  NAV_GROUPS, activeGroupId, readCollapsedGroups, writeCollapsedGroups, navPathFor } from './navGroups';

// ---------------------------------------------------------------------------
// Nav item config
// ---------------------------------------------------------------------------

interface NavItem {
  label: string;
  path: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;
}

// WHO SEES EACH ITEM is not written here. It is config/routeAccess.ts — the
// map every route is gated by (Deep-test ADM-006) — so the sidebar can never
// offer a page the route then refuses, nor hide one the route would open.
const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: HomeIcon },
  { label: 'Spaces', path: '/spaces', icon: BuildingOfficeIcon },
  // Employee B3; Bug_121: offered to everyone who can book (NAV_OFFER).
  { label: 'My bookings', path: '/my-bookings', icon: BookmarkSquareIcon },
  { label: 'Helpdesk', path: '/helpdesk', icon: TicketIcon },
  { label: 'Maintenance', path: '/maintenance', icon: WrenchScrewdriverIcon },
  { label: 'Assets', path: '/assets', icon: CpuChipIcon },
  { label: 'Scan Asset', path: '/scan', icon: QrCodeIcon },
  { label: 'Visitors', path: '/visitors', icon: UserGroupIcon },
  { label: 'Leases', path: '/leases', icon: DocumentTextIcon },
  { label: 'Contractors', path: '/contractors', icon: TruckIcon },
  { label: 'Tenants', path: '/tenants', icon: UserIcon },
  { label: 'Parking', path: '/parking', icon: Square3Stack3DIcon },
  { label: 'Wellbeing', path: '/wellbeing', icon: HeartIcon },
  { label: 'IoT / Shop Floor', path: '/iot', icon: CpuChipIcon },
  { label: 'Energy', path: '/energy', icon: BoltIcon },
  { label: 'Compliance', path: '/compliance', icon: ShieldCheckIcon },
  { label: 'Safety & Emergency', path: '/safety', icon: ShieldExclamationIcon },
  // FM Technician C14 (D11): red, for the technician only (TECH_ONLY_NAV).
  { label: 'Report incident', path: '/tech/incident', icon: ExclamationTriangleIcon },
  { label: 'Hybrid Work', path: '/hybrid', icon: CalendarDaysIcon },
  { label: 'Marketplace', path: '/marketplace/integrations', icon: ShoppingCartIcon },
  { label: 'Checklists', path: '/maintenance/checklists', icon: ClipboardDocumentListIcon },
  { label: 'Analytics', path: '/analytics', icon: ChartBarIcon },
  { label: 'Admin', path: '/admin', icon: Cog6ToothIcon },
  // 🔴 THE PAGE NOTHING LINKED TO. User Management has held bulk CSV invite
  // since 11 Sep and had no entry here, no card, no link anywhere in the
  // product — reachable only by typing /admin/users. A tester filed the
  // feature as "not implemented". Gated on the permission the page's own
  // writes need, so it is not offered to somebody who would be refused.
  { label: 'Users', path: '/admin/users', icon: UsersIcon },
  { label: 'Reception set-up', path: '/admin/reception', icon: UsersIcon },
  { label: 'Organisations', path: '/super-admin/organisations', icon: BuildingOffice2Icon },
  // The support inbox. Beside Organisations because both are the platform
  // operator's view of every tenant rather than anything inside one.
  { label: 'Support inbox', path: '/super-admin/support', icon: ChatBubbleLeftRightIcon },
  // Public data requests that reached no organisation, so no tenant sees them.
  // Bug_117: named for what they are, in the platform's Privacy group.
  { label: 'Unmatched privacy requests', path: '/super-admin/data-requests', icon: InboxIcon },
];

/** The employee shell's icons (Employee rebuild C General), by route. The
 *  items and their order are data in config/employeeExperience. */
const EMPLOYEE_ICONS: Record<string, NavItem['icon']> = {
  '/my-day': HomeIcon,
  '/book': BuildingOfficeIcon,
  '/my-bookings': ListBulletIcon,
  '/office-days': CalendarDaysIcon,
  '/whos-in': UserIcon,
  '/my-visitors': UserGroupIcon,
  '/my-requests': TicketIcon,
  '/my-equipment': ComputerDesktopIcon,
  '/wellbeing': HeartIcon,
  '/my-safety': ShieldCheckIcon,
  '/notifications': BellIcon,
  '/settings': Cog6ToothIcon,
};

/** The front desk's icons (Reception C2), by item id in config/receptionExperience. */
const DESK_ICONS: Record<string, NavItem['icon']> = {
  home: HomeIcon, alerts: BellIcon, checkin: QrCodeIcon, visitors: UserGroupIcon, prereg: CalendarDaysIcon,
  approvals: ShieldCheckIcon, watchlist: ExclamationTriangleIcon, mail: ListBulletIcon, passes: ListBulletIcon,
  keys: ListBulletIcon, lost: ListBulletIcon, rooms: BuildingOfficeIcon, parking: BuildingOfficeIcon,
  catering: ListBulletIcon, emergency: ExclamationTriangleIcon, helpdesk: TicketIcon, profile: UserIcon,
};

/** The technician shell's icons (FM Technician C1), by item id — an item's
 *  path moves from the existing page to the technician page as slices ship. */
export const TECH_ICONS: Record<string, NavItem['icon']> = {
  home: HomeIcon,
  alerts: BellIcon,
  jobs: WrenchScrewdriverIcon,
  tickets: TicketIcon,
  scan: QrCodeIcon,
  rounds: ClipboardDocumentListIcon,
  fault: MegaphoneIcon,
  // R3: permits keep the FM's document, locks a padlock, emergency steps the
  // safety shield, tools a wrench (jobs has the wrench and screwdriver).
  permits: DocumentTextIcon,
  locks: LockClosedIcon,
  emergency: ShieldExclamationIcon,
  tools: WrenchIcon,
  incident: ExclamationTriangleIcon,
  handover: ArrowsRightLeftIcon,
  lead: ClipboardDocumentCheckIcon,
  'my-work': ChartPieIcon,
  profile: UserCircleIcon,
  book: BuildingOfficeIcon,
  visitors: UserGroupIcon,
  requests: TicketIcon,
};

/** The FM shell's icons (FM Manager rebuild C0.1), by route. */
export const FM_ICONS: Record<string, NavItem['icon']> = {
  '/fm': HomeIcon,
  '/fm/tickets': TicketIcon,
  '/fm/spot-checks': ClipboardDocumentCheckIcon,
  '/fm/logbooks': BookOpenIcon,
  // W5: the prototype's calendar and cube.
  '/fm/ppm': CalendarDaysIcon,
  '/fm/assets': CubeIcon,
  // W6: the prototype's document, flame and warning triangle.
  '/fm/permits': DocumentTextIcon,
  '/fm/drills': FireIcon,
  '/fm/incidents': ExclamationTriangleIcon,
  // FM Technician C24 / C25.
  '/fm/technicians': UsersIcon,
  '/fm/designated-persons': IdentificationIcon,
  // FM Technician C27 (R3): the same icons as the technician's items.
  '/fm/tools': WrenchIcon,
  '/fm/locks': LockClosedIcon,
  '/hybrid': CalendarDaysIcon,
  '/helpdesk': TicketIcon,
  '/maintenance': WrenchScrewdriverIcon,
  '/assets': CpuChipIcon,
  '/scan': QrCodeIcon,
  '/maintenance/checklists': ClipboardDocumentListIcon,
  '/spaces': BuildingOfficeIcon,
  '/energy': BoltIcon,
  '/iot': CpuChipIcon,
  '/safety': ShieldExclamationIcon,
  '/wellbeing': HeartIcon,
  '/contractors': TruckIcon,
  '/compliance': ShieldCheckIcon,
  // W9: the compliance calendar keeps the shield.
  '/fm/compliance': ShieldCheckIcon,
  // W10: the reports and the KPI board.
  '/fm/reports': DocumentChartBarIcon,
  '/fm/kpis': PresentationChartLineIcon,
  '/analytics': ChartBarIcon,
  '/fm/settings': Cog6ToothIcon,
};

/**
 * The two counts on the employee's sidebar (prototype C0): my open requests
 * beside My requests, unread notifications beside Notifications. Each is its
 * own component, mounted only in the employee's menu, so no other role's
 * sidebar sends a request it did not send before.
 *
 * 🔴 `mine=true`: the count is the person's OWN open requests even when they
 * hold helpdesk:view_all (a custom role that still gets this shell). The
 * unread count rides the bell's own query — same key, same URL — so the two
 * numbers on screen are one request and cannot disagree.
 */
function CountPill({ n, label }: { n: number; label: string }) {
  if (n <= 0) return null;
  return (
    <span className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-brand-600 px-1.5 text-[10px] font-semibold text-white"
          aria-label={label} data-testid="nav-count">
      {n > 99 ? '99+' : n}
    </span>
  );
}

/** An FM menu count (C0.1): red for tickets when any is breached. Loaded
 *  after the menu; until then nothing is drawn, so there is no layout jump
 *  beyond the bubble itself appearing. */
function FmCount({ k }: { k: FmCountKey }) {
  const { data } = useFmCounts();
  const n = data?.[k] ?? 0;
  if (n <= 0) return null;
  const red = k === 'tickets' && (data?.tickets_breached ?? 0) > 0;
  return (
    <span className={`ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold text-white ${
      red ? 'bg-danger-600' : 'bg-brand-600'}`}
          aria-label={red ? `${n} open, some breached` : `${n}`} data-testid={`fm-count-${k}`}>
      {n > 99 ? '99+' : n}
    </span>
  );
}

function MyOpenRequestsCount() {
  const user = useAuthStore((s) => s.user);
  const q = useQuery({
    queryKey: ['helpdesk', 'tickets', 'mine-open', 'count'],
    queryFn: () => apiGet<{ total?: number }>('/helpdesk/tickets?mine=true&status=all_open&limit=1'),
    enabled: hasPermFor(user, 'helpdesk:view'),
    staleTime: 60_000,
  });
  const n = q.data?.total ?? 0;
  return <CountPill n={n} label={`${n} open`} />;
}

/** The bell's first page — NotificationCentre's PAGE. */
const BELL_LIMIT = 20;

function UnreadCount() {
  const limit = BELL_LIMIT;
  const q = useQuery({
    queryKey: ['notifications', limit],
    queryFn: () => apiGet<{ unread_count: number }>(`/notifications/?limit=${limit}`),
    refetchInterval: 30000,
  });
  const n = q.data?.unread_count ?? 0;
  return <CountPill n={n} label={`${n} unread`} />;
}

// ---------------------------------------------------------------------------
// Sidebar component
// ---------------------------------------------------------------------------

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onMobileClose?: () => void;
}

export default function Sidebar({ collapsed, onToggle, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  // Employee rebuild C General: the employee experience gets its own grouped
  // menu (config/employeeExperience); every other role keeps NAV_ITEMS.
  const employee = isEmployeeExperience(user);
  // FM Manager rebuild C0.1: the FM's own grouped menu.
  const fm = isFmExperience(user);
  const fmShell = useFmShell();
  // FM Technician rebuild C1: the technician's own grouped menu.
  const tech = !fm && !employee && isTechShell(user);
  // Reception rebuild C2: the front desk's own grouped menu.
  const reception = !fm && !employee && !tech && isReceptionShell(user);
  const labelled = employee || fm || tech || reception;
  const filteredItems = NAV_ITEMS.filter((item) => canOfferInNav(user, item.path));

  const here = navPathFor(location.pathname);
  const fmHere = fm ? fmActivePath(here) : null;
  const techGroups = useMemo(
    () => (tech ? techNavGroups(user, (p) => canOfferInNav(user, p)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tech, user?.role, user?.id, user?.permissions]);
  const techHere = tech
    ? techActivePath(here, techGroups.flatMap((g) => g.items.map((i) => i.path))) : null;
  const deskGroups = useMemo(
    () => (reception ? receptionNavGroups(user, (p) => canOfferInNav(user, p)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reception, user?.role, user?.id, user?.permissions]);
  const deskHere = reception
    ? techActivePath(here, deskGroups.flatMap((g) => g.items.map((i) => i.path))) : null;
  const isActive = (path: string) =>
    fm ? fmHere === path : tech ? techHere === path : reception ? deskHere === path
      : here === path || here.startsWith(path + '/');

  const activeGroup = activeGroupId(here);

  // The active item's group is ALWAYS open, whatever was stored. Landing on a
  // page whose nav entry is folded away leaves you unable to see where you are.
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>(
    () => readCollapsedGroups().filter((id) => id !== activeGroup));

  const toggleGroup = (id: string) => {
    setCollapsedGroups((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      writeCollapsedGroups(next);
      return next;
    });
  };

  // A group appears only if the EXISTING per-item filter left it something to
  // show. No second permission system — this one already knows the answer.
  const groups = useMemo(() => {
    const byPath = new Map(filteredItems.map((i) => [i.path, i]));
    // The employee's own groups — My Day, Workspace, Services — each item
    // still through canOfferInNav. Headings such as "Real estate" over a
    // parking module describe the estate, not their day.
    if (fm) {
      return fmNavGroups(fmShell.data?.modules, (p) => canOfferInNav(user, p))
        .map((g) => ({
          id: `fm-${g.id}`, heading: g.heading, paths: g.items.map((i) => i.path),
          items: g.items.map((i) => ({ label: i.label, path: i.path, count: i.count,
                                       icon: FM_ICONS[i.path] ?? HomeIcon })),
        }));
    }
    if (tech) {
      return techGroups.map((g) => ({
        id: `tech-${g.id}`, heading: g.heading, paths: g.items.map((i) => i.path),
        items: g.items.map((i) => ({ label: i.label, path: i.path, icon: TECH_ICONS[i.id] ?? HomeIcon })),
      }));
    }
    if (reception) {
      return deskGroups.map((g) => ({
        id: `desk-${g.id}`, heading: g.heading, paths: g.items.map((i) => i.path),
        items: g.items.map((i) => ({ label: i.label, path: i.path, icon: DESK_ICONS[i.id] ?? HomeIcon })),
      }));
    }
    if (employee) {
      return EMPLOYEE_NAV_GROUPS
        .map((g) => ({
          id: `emp-${g.id}`, heading: g.heading, paths: g.items.map((i) => i.path),
          items: g.items
            .filter((i) => canOfferInNav(user, i.path))
            .map((i) => ({ label: i.label, path: i.path, icon: EMPLOYEE_ICONS[i.path] ?? HomeIcon })),
        }))
        .filter((g) => g.items.length > 0);
    }
    return NAV_GROUPS
      .map((g) => ({
        ...g,
        items: g.paths.map((p) => byPath.get(p)).filter(Boolean) as NavItem[],
      }))
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.id, location.pathname, fmShell.data?.modules, techGroups, deskGroups]);

  return (
    <aside
      className={`flex h-full flex-col bg-surface-inverse transition-all duration-300 ${
        collapsed ? 'w-sidebar-collapsed' : 'w-sidebar'
      }`}
    >
      {/* Logo */}
      <div className="flex h-16 items-center gap-3 border-b border-line-inverse px-4">
        <SpacioraLogo size={32} className="flex-shrink-0" />
        {!collapsed && (
          <span className="text-lg font-semibold text-content-inverse tracking-tight">
            Spaciora
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto scrollbar-hide px-3 py-4">
        {groups.map((group, gi) => {
          const isCollapsed = collapsedGroups.includes(group.id);
          const showItems = collapsed || group.heading === null || labelled || !isCollapsed;
          return (
            <div key={group.id} className={gi > 0 ? 'mt-4' : ''}>
              {/* In the RAIL there is no room for a heading, and folding items
                  away behind one nobody can read would hide them for good. So
                  the rail shows every item flat, with a hairline where a
                  heading would have been — the grouping is still felt. */}
              {collapsed && gi > 0 && group.heading !== null && (
                <div className="mx-2 mb-3 border-t border-line-inverse" />
              )}

              {/* The employee's headings are labels, not folders: their
                  menu is nine items and nothing in it should hide. */}
              {!collapsed && group.heading && labelled && (
                <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-content-muted"
                   data-testid={`nav-group-${group.id}`}>
                  {group.heading}
                </p>
              )}

              {!collapsed && group.heading && !labelled && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={!isCollapsed}
                  aria-controls={`nav-group-${group.id}`}
                  data-testid={`nav-group-${group.id}`}
                  className="flex w-full items-center justify-between rounded px-3 py-1.5
                    text-xs font-semibold uppercase tracking-wider text-content-muted
                    transition-colors hover:text-content-inverse-secondary"
                >
                  <span>{group.heading}</span>
                  <ChevronDownIcon
                    className={`h-3.5 w-3.5 transition-transform duration-200 ${
                      isCollapsed ? '-rotate-90' : ''}`}
                  />
                </button>
              )}

              {showItems && (
                <ul className="mt-1 space-y-1" id={`nav-group-${group.id}`}
                    data-testid={`nav-group-items-${group.id}`}>
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);

                    return (
                      <li
                        key={item.path}
                        className="relative"
                        onMouseEnter={() => collapsed ? setHoveredItem(item.path) : undefined}
                        onMouseLeave={() => setHoveredItem(null)}
                      >
                        <NavLink
                          to={item.path}
                          onClick={onMobileClose}
                          // DSH-042: collapsed, the icon is all there is — the
                          // label must still name the link.
                          aria-label={collapsed ? item.label : undefined}
                          className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                            active
                              ? 'bg-brand-700/10 text-brand-500'
                              : item.path === TECH_INCIDENT
                                // C14 (D11): the red one, word and icon.
                                ? 'text-danger-300 hover:bg-danger-700/20 hover:text-danger-200'
                                : 'text-content-subtle hover:bg-surface-inverse-raised hover:text-content-inverse-secondary'
                          }`}
                          data-testid={item.path === TECH_INCIDENT ? 'nav-report-incident' : undefined}
                        >
                          <Icon className={`h-5 w-5 flex-shrink-0 ${active ? 'text-brand-500' : ''}`} />
                          {!collapsed && <span>{item.label}</span>}
                          {!collapsed && employee && item.path === '/my-requests' && <MyOpenRequestsCount />}
                          {!collapsed && fm && 'count' in item && typeof item.count === 'string' && <FmCount k={item.count as FmCountKey} />}
                          {active && (
                            <div className="absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r bg-brand-500" />
                          )}
                        </NavLink>

                        {/* Tooltip for collapsed state */}
                        {collapsed && hoveredItem === item.path && (
                          <div className="absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 rounded-lg bg-surface-inverse-raised px-3 py-2 text-sm font-medium text-content-inverse shadow-lg">
                            {item.label}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      {/* Settings at bottom.
          Outside the groups on purpose: it is personal preferences, not
          administration, and it carries no permission. Grouping it under ADMIN
          showed that heading to every employee with a single unrelated link
          beneath it. The employee's footer adds Notifications above it, with
          the unread count (Employee rebuild C General). */}
      <div className="space-y-1 border-t border-line-inverse px-3 py-3">
        {/* C0.1: who is signed in, and for which client. */}
        {fm && !collapsed && user && (
          <p className="px-3 pb-1 text-[11px] leading-snug text-content-subtle" data-testid="fm-signed-in">
            Signed in: {user.first_name} {user.last_name}
            {fmShell.data?.client_name ? ` · Client: ${fmShell.data.client_name}` : ''}
          </p>
        )}
        {(employee
          ? EMPLOYEE_NAV_FOOTER.filter((i) => canOfferInNav(user, i.path))
          : [{ label: 'Settings', path: '/settings' }]
        ).map((entry) => {
          const Icon = EMPLOYEE_ICONS[entry.path] ?? Cog6ToothIcon;
          return (
            <NavLink
              key={entry.path}
              to={entry.path}
              onClick={onMobileClose}
              data-testid={entry.path === '/settings' ? 'nav-settings' : `nav-footer-${entry.path.slice(1)}`}
              aria-label={collapsed ? entry.label : undefined}
              title={collapsed ? entry.label : undefined}
              className={`relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive(entry.path)
                  ? 'bg-brand-700/10 text-brand-500'
                  : 'text-content-subtle hover:bg-surface-inverse-raised hover:text-content-inverse-secondary'
              }`}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span>{entry.label}</span>}
              {!collapsed && employee && entry.path === '/notifications' && <UnreadCount />}
            </NavLink>
          );
        })}
      </div>

      {/* Collapse toggle (desktop only) */}
      <div className="hidden border-t border-line-inverse px-3 py-3 lg:block">
        <button
          onClick={onToggle}
          className="flex w-full items-center justify-center rounded-lg px-3 py-2 text-content-muted transition-colors hover:bg-surface-inverse-raised hover:text-content-faint"
          // DSH-042: was "Next" in both states.
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
          ) : (
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          )}
        </button>
      </div>
    </aside>
  );
}
